// Runtime configuration, read from window.__MCPGRAPH__ (set by /config.js,
// which entrypoint.sh rewrites at container start). Falls back to sane dev
// defaults when the global is absent (e.g. unit tests).

export interface RuntimeConfig {
  /** Base URL of mcpgraph-gateway. Empty string => same origin (relative). */
  GATEWAY_URL: string;
  /** Base URL prepended to the workspace-assistant metrics fetch (AF6). Empty string
   *  (the default) => same origin, so the request rides the `/assistant-api/` path that
   *  nginx (prod) / the vite dev proxy reverse-proxies to the assistant service. Set it
   *  only for a cross-origin direct-to-assistant deploy. */
  ASSISTANT_URL: string;
  /** Boot the MSW mock layer instead of a real backend. */
  USE_MOCKS: boolean;
  /** Optional bearer token, injected by the embedding GUI for the live gateway. */
  BEARER?: string;
  /** App version for the sidebar footer, injected by the embedding GUI (H26).
   *  Absent in the standalone build, which falls back to the package version. */
  APP_VERSION?: string;
}

declare global {
  interface Window {
    __MCPGRAPH__?: Partial<RuntimeConfig>;
  }
}

const fromWindow: Partial<RuntimeConfig> =
  (typeof window !== 'undefined' && window.__MCPGRAPH__) || {};

/**
 * Mocks are OPT-IN ONLY and must NEVER auto-start in a production/embedded build (H29/H30).
 *
 * The actual mock code (MSW worker + handlers + fixtures) only exists in the bundle when the
 * build-time constant `__USE_MOCKS__` is true — i.e. a deliberate `VITE_USE_MOCKS=1` build; a normal
 * production build tree-shakes it out entirely. This function decides whether mocks RUN:
 *   1. the embedding host explicitly sets `window.__MCPGRAPH__.USE_MOCKS` (true forces on,
 *      false forces off) — wins over everything;
 *   2. otherwise follow the build-time switch `__USE_MOCKS__` (true only in a mock build).
 * The default is FALSE — a normal build with no override talks to the REAL backend, never fixtures.
 */
function resolveUseMocks(): boolean {
  if (fromWindow.USE_MOCKS === true) return true;
  if (fromWindow.USE_MOCKS === false) return false;
  return __USE_MOCKS__;
}

export const config: RuntimeConfig = {
  GATEWAY_URL: fromWindow.GATEWAY_URL ?? '',
  ASSISTANT_URL: fromWindow.ASSISTANT_URL ?? '',
  USE_MOCKS: resolveUseMocks(),
  BEARER: fromWindow.BEARER,
};

/** Build an absolute API URL respecting the configured server base. */
export function apiUrl(path: string): string {
  const base = config.GATEWAY_URL.replace(/\/$/, '');
  return `${base}${path}`;
}

/** Build the workspace-assistant metrics URL (AF6). Same-origin by default (the
 *  `/assistant-api/` prefix is reverse-proxied to the assistant); an ASSISTANT_URL
 *  override targets the service directly. */
export function assistantUrl(path: string): string {
  const base = config.ASSISTANT_URL.replace(/\/$/, '');
  return `${base}${path}`;
}

/** Build a ws(s):// URL for the same origin/base as the API. */
export function wsUrl(path: string): string {
  const base = config.GATEWAY_URL;
  if (base) return base.replace(/^http/, 'ws').replace(/\/$/, '') + path;
  // Same-origin: derive from the current location.
  if (typeof window !== 'undefined') {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${window.location.host}${path}`;
  }
  return `ws://localhost${path}`;
}
